/**
 * Pure utility functions for deed poll name comparison logic
 * No external dependencies - safe for unit testing
 */

/**
 * @description Parse a name that may contain pinyin in parentheses
 * @param fullName The full name from RDB, e.g. "Teh Hao He, Kopi (Zheng Hao He, Kopi)"
 * @returns { mainName: string, pinyinName: string | null }
 */
export const parseNameWithPinyin = (fullName: string): { mainName: string; pinyinName: string | null } => {
  const match = (fullName ?? '').match(/^(.+?)\s*\(([^)]+)\)\s*$/);
  if (match) {
    return {
      mainName: match[1].trim(),
      pinyinName: match[2].trim()
    };
  }
  return {
    mainName: (fullName ?? '').trim(),
    pinyinName: null
  };
};

/**
 * @description Normalize a name for comparison (trim and convert to uppercase)
 * @param name The name to normalize
 * @returns Normalized name string
 */
export const normalizeForComparison = (name: string): string => {
  return (name ?? "").trim().toUpperCase();
};

/**
 * @description Locale sensitive, case insensitive name comparison
 * @param name1 First name to compare
 * @param name2 Second name to compare
 * @returns true if names are equal (case insensitive)
 */
export const namesAreEqual = (name1: string, name2: string): boolean => {
  const a = normalizeForComparison(name1);
  const b = normalizeForComparison(name2);
  return a.localeCompare(b, "en", { sensitivity: "base" }) === 0;
};

/**
 * @description Check if deed poll is required based on name comparison.
 * Pinyin comparison is secondary — only evaluated when both RDB and user side provide it.
 * @param nameFilledIn The name from MyInfo or user input (may contain pinyin in parentheses)
 * @param hanyupinyinname The hanyu pinyin name from MyInfo (empty if not retrieved)
 * @param rdbName The full name from RDB (may contain pinyin in parentheses)
 * @returns true if names match (deed poll NOT required), false if names don't match (deed poll required)
 */
export const checkDeedPollNameMatch = (
  nameFilledIn: string,
  hanyupinyinname: string,
  rdbName: string
): boolean => {
  const { mainName: rdbMainName, pinyinName: rdbPinyinName } = parseNameWithPinyin(rdbName);
  const { mainName: inputMainName, pinyinName: inputPinyinName } = parseNameWithPinyin(nameFilledIn);

  const mainNameMatches = namesAreEqual(inputMainName, rdbMainName);

  const userPinyinName = hanyupinyinname || inputPinyinName;

  if (rdbPinyinName && userPinyinName) {
    const pinyinNameMatches = namesAreEqual(userPinyinName, rdbPinyinName);
    return mainNameMatches && pinyinNameMatches;
  }

  return mainNameMatches;
};
